import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Title from "../../../../components/Title";
import { enums } from "../../../../shim/jsbridge";
import { NETWORK_URLS } from "../../../network/consts";
import { URLS } from "../../../samples_cloud/consts";
import { generateEndpoint } from "../../util";
import { DYNAMIC_ANALYSIS_TABS_ID } from "../common/dynamicanalysis/network/AnalysisTabs";
import RlCloudSandboxAndroidBehavioral from "./android_behavioral/RlCloudSandboxAndroidBehavioral";
import RlCloudSandboxBehavioral from "./behavioral/RlCloudSandboxBehavioral";
import RlCloudSandboxDroppedFiles from "./droppedfiles/RlCloudSandboxDroppedFiles";
import RlCloudSandboxMalwareConfigurations from "./malware_configurations/RlCloudSandboxMalwareConfigurations";
import RlCloudSandboxMitreAttack from "./mitreattack/RlCloudSandboxMitreAttack";
import RlCloudSandboxNetwork from "./network/RlCloudSandboxNetwork";
import RlCloudSandboxSigmaDetections from "./sigmadetections/RlCloudSandboxSigmaDetections";
import RlCloudSandboxSignatures from "./signatures/RlCloudSandboxSignatures";
import RlCloudSandboxApk from "./static/RlCloudSandboxApk";
import RlCloudSandboxSuricataAlerts from "./suricata_alerts/RlCloudSandboxSuricataAlerts";
import RLCloudSandboxYara from "./yara/RLCloudSandboxYara";
import { getTabDataCounts } from "./utils";

const { dynamic_analysis_routes: dynamicAnalysisSections } = enums || {};

/**
 * Navigate to rl_dynamic_route dynamically.
 * If sourceId is present go to sample summary screen,
 * otherwise go to sample summary cloud details screen.
 */
const navigateToTab = (hash, sourceId, section, url) => {
  if (url)
    return NETWORK_URLS.NETWORK_DETAILS_CLOUD_DYNAMIC(url, hash, section);
  return sourceId
    ? generateEndpoint(hash, sourceId, "rl_dynamic_analysis", section)
    : URLS.SAMPLE_DETAILS_CLOUD_DYNAMIC(hash, section);
};

const RLCloudSandboxTabs = ({
  hash,
  sourceId,
  activeSection,
  activeReport,
  data,
  historyAnalysis,
  isMergedSelected,
  isLatestFetched,
  isStoredReport,
  isPrivate,
  isNetworkReport = false
}) => {
  const [activeTabState, setActiveTabState] = useState(
    dynamicAnalysisSections.SIGNATURES
  );
  const { url } = useParams(); // only on network page
  const navigate = useNavigate();

  const {
    behavioral,
    network,
    suricata_alerts,
    dropped_files,
    process_tree,
    mitre_attack,
    snort_alerts,
    sigma_detections,
    malware_configurations,
    signatures,
    yara,
    android_behavioral,
    apk
  } = data || {};

  const tabDataCounts = useMemo(() => getTabDataCounts(data), [data]);

  const activeAnalysis = historyAnalysis.find(
    report => report.analysis_id === activeReport
  );
  const analysisTime = activeAnalysis?.analysis_time;
  const analysisId = isMergedSelected
    ? historyAnalysis?.[0]?.analysis_id
    : activeAnalysis?.analysis_id;

  const activeReportTitle = analysisTime
    ? moment.utc(analysisTime).format("YYYY-MM-DD HH:mm UTC")
    : "All Reports";

  const showAndroidSpecificTabs =
    activeAnalysis != null
      ? activeAnalysis?.platform === "android12"
      : historyAnalysis?.some(analysis => analysis?.platform === "android12");
  const showAndroidNonspecificTabs =
    activeAnalysis != null
      ? activeAnalysis.platform !== "android12"
      : historyAnalysis?.some(report => report.platform !== "android12");

  useEffect(() => {
    if (
      activeSection &&
      (activeSection !== dynamicAnalysisSections.ANDROID_BEHAVIORAL ||
        showAndroidSpecificTabs)
    ) {
      setActiveTabState(activeSection);
    }
  }, [activeSection, showAndroidSpecificTabs]);

  return (
    <Tab.Container
      id="navigation-tab"
      activeKey={activeTabState}
      onSelect={activeTab => {
        setActiveTabState(activeTab);
        navigate(navigateToTab(hash, sourceId, activeTab, url), {
          preventScrollReset: true
        });
      }}
    >
      <Nav
        id={DYNAMIC_ANALYSIS_TABS_ID}
        variant="tabs"
        className="nav-tabs--bordered"
      >
        <LinkContainer
          to={navigateToTab(
            hash,
            sourceId,
            dynamicAnalysisSections.SIGNATURES,
            url
          )}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.SIGNATURES}>
              Signatures ({tabDataCounts[dynamicAnalysisSections.SIGNATURES]})
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>

        {showAndroidNonspecificTabs && (
          <LinkContainer
            to={navigateToTab(
              hash,
              sourceId,
              dynamicAnalysisSections.MALWARE_CONFIGURATIONS,
              url
            )}
          >
            <Nav.Item>
              <Nav.Link
                eventKey={dynamicAnalysisSections.MALWARE_CONFIGURATIONS}
              >
                Malware Config (
                {tabDataCounts[dynamicAnalysisSections.MALWARE_CONFIGURATIONS]})
              </Nav.Link>
            </Nav.Item>
          </LinkContainer>
        )}
        <LinkContainer
          to={navigateToTab(
            hash,
            sourceId,
            dynamicAnalysisSections.NETWORK_ANALYSIS,
            url
          )}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.NETWORK_ANALYSIS}>
              Network ({tabDataCounts[dynamicAnalysisSections.NETWORK_ANALYSIS]}
              )
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>

        <LinkContainer
          to={navigateToTab(
            hash,
            sourceId,
            dynamicAnalysisSections.SURICATA_ALERTS,
            url
          )}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.SURICATA_ALERTS}>
              Suricata ({tabDataCounts[dynamicAnalysisSections.SURICATA_ALERTS]}
              )
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>

        <LinkContainer
          to={navigateToTab(
            hash,
            sourceId,
            dynamicAnalysisSections.SIGMA_DETECTIONS,
            url
          )}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.SIGMA_DETECTIONS}>
              Sigma ({tabDataCounts[dynamicAnalysisSections.SIGMA_DETECTIONS]})
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>
        {showAndroidSpecificTabs && (
          <LinkContainer
            to={navigateToTab(hash, sourceId, dynamicAnalysisSections.APK, url)}
          >
            <Nav.Item>
              <Nav.Link eventKey={dynamicAnalysisSections.APK}>
                APK ({tabDataCounts[dynamicAnalysisSections.APK]})
              </Nav.Link>
            </Nav.Item>
          </LinkContainer>
        )}

        {showAndroidSpecificTabs && (
          <LinkContainer
            to={navigateToTab(
              hash,
              sourceId,
              dynamicAnalysisSections.ANDROID_BEHAVIORAL,
              url
            )}
          >
            <Nav.Item>
              <Nav.Link eventKey={dynamicAnalysisSections.ANDROID_BEHAVIORAL}>
                Android Behavioral (
                {tabDataCounts[dynamicAnalysisSections.ANDROID_BEHAVIORAL]})
              </Nav.Link>
            </Nav.Item>
          </LinkContainer>
        )}
        {showAndroidNonspecificTabs && (
          <LinkContainer
            to={navigateToTab(
              hash,
              sourceId,
              dynamicAnalysisSections.BEHAVIORAL,
              url
            )}
          >
            <Nav.Item>
              <Nav.Link eventKey={dynamicAnalysisSections.BEHAVIORAL}>
                Behavioral
                {isMergedSelected && !isLatestFetched
                  ? ""
                  : ` (${tabDataCounts[dynamicAnalysisSections.BEHAVIORAL]})`}
              </Nav.Link>
            </Nav.Item>
          </LinkContainer>
        )}

        <LinkContainer
          to={navigateToTab(
            hash,
            sourceId,
            dynamicAnalysisSections.DROPPED_FILES,
            url
          )}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.DROPPED_FILES}>
              Dropped Files (
              {tabDataCounts[dynamicAnalysisSections.DROPPED_FILES]})
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>
        <LinkContainer
          to={navigateToTab(
            hash,
            sourceId,
            dynamicAnalysisSections.MITRE_ATTACK,
            url
          )}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.MITRE_ATTACK}>
              MITRE ATT&amp;CK (
              {tabDataCounts[dynamicAnalysisSections.MITRE_ATTACK]})
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>
        <LinkContainer
          to={navigateToTab(hash, sourceId, dynamicAnalysisSections.YARA, url)}
        >
          <Nav.Item>
            <Nav.Link eventKey={dynamicAnalysisSections.YARA}>
              YARA ({tabDataCounts[dynamicAnalysisSections.YARA]})
            </Nav.Link>
          </Nav.Item>
        </LinkContainer>
      </Nav>
      <Tab.Content>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.SIGNATURES}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxSignatures
            data={signatures}
            reportTitle={activeReportTitle}
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.MALWARE_CONFIGURATIONS}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxMalwareConfigurations
            configs={malware_configurations}
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.NETWORK_ANALYSIS}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxNetwork
            networkData={network}
            reportTitle={activeReportTitle}
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.SURICATA_ALERTS}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxSuricataAlerts
            data={[...(suricata_alerts ?? []), ...(snort_alerts ?? [])]}
            reportTitle={activeReportTitle}
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.SIGMA_DETECTIONS}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxSigmaDetections
            data={sigma_detections}
            reportTitle={activeReportTitle}
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.BEHAVIORAL}
          mountOnEnter
          unmountOnExit
        >
          {!isStoredReport ? (
            <RlCloudSandboxBehavioral
              mergedReportActive={isMergedSelected}
              latestReportReady={isLatestFetched}
              behavioralData={behavioral}
              processTreeData={process_tree}
              reportTitle={activeReportTitle}
            />
          ) : (
            <>
              <Title Level="h2" text="Behavioral Analysis (Latest Report)" />
              <b>Behavioral Data for Latest Report Not Stored Locally.</b>
            </>
          )}
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.DROPPED_FILES}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxDroppedFiles
            analysisId={analysisId}
            droppedFiles={dropped_files}
            hash={hash}
            isStoredReport={isStoredReport}
            isPrivate={isPrivate}
            isMergedReport={isMergedSelected}
            isNetworkReport={isNetworkReport}
            reportTitle={activeReportTitle}
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.MITRE_ATTACK}
          mountOnEnter
          unmountOnExit
        >
          <RlCloudSandboxMitreAttack
            attackData={mitre_attack}
            disablePopup
            disableShowAll
          />
        </Tab.Pane>
        <Tab.Pane
          eventKey={dynamicAnalysisSections.YARA}
          mountOnEnter
          unmountOnExit
        >
          <RLCloudSandboxYara yaraData={yara} reportTitle={activeReportTitle} />
        </Tab.Pane>
        {showAndroidSpecificTabs && (
          <Tab.Pane
            eventKey={dynamicAnalysisSections.APK}
            mountOnEnter
            unmountOnExit
          >
            <RlCloudSandboxApk data={apk} reportTitle={activeReportTitle} />
          </Tab.Pane>
        )}

        {showAndroidSpecificTabs && (
          <Tab.Pane
            eventKey={dynamicAnalysisSections.ANDROID_BEHAVIORAL}
            mountOnEnter
            unmountOnExit
          >
            <RlCloudSandboxAndroidBehavioral
              data={android_behavioral}
              reportTitle={activeReportTitle}
            />
          </Tab.Pane>
        )}
      </Tab.Content>
    </Tab.Container>
  );
};

RLCloudSandboxTabs.propTypes = {
  activeReport: PropTypes.string,
  data: PropTypes.shape({
    dropped_files: PropTypes.array,
    network: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    behavioral: PropTypes.array,
    process_tree: PropTypes.array,
    mitre_attack: PropTypes.object,
    snort_alerts: PropTypes.array,
    sigma_detections: PropTypes.array,
    suricata_alerts: PropTypes.array,
    yara: PropTypes.array
  }),
  hash: PropTypes.string,
  sourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activeSection: PropTypes.string,
  historyAnalysis: PropTypes.array,
  isStoredReport: PropTypes.bool,
  isPrivate: PropTypes.bool,
  isNetworkReport: PropTypes.bool,
  isMergedSelected: PropTypes.bool,
  isLatestFetched: PropTypes.bool
};

export default RLCloudSandboxTabs;
